import { useCallback, useRef, useEffect } from "react";

const RECAPTCHA_SITE_KEY = "6LesnWorAAAAALxJu79S1dz3GQIoMKnJgJdkeZ-C";

export const useRecaptcha = () => {
  const recaptchaReadyRef = useRef(false);
  const initAttemptedRef = useRef(false);

  // Detectar si estamos en un iframe
  const isInIframe = () => {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  };

  const loadRecaptchaScript = useCallback(() => {
    // Si el script ya está cargado en el HTML, solo esperar a que esté listo
    return new Promise((resolve) => {
      const initRecaptcha = () => {
        if (window.grecaptcha && window.grecaptcha.ready) {
          window.grecaptcha.ready(() => {
            recaptchaReadyRef.current = true;
            resolve();
          });
        } else {
          setTimeout(initRecaptcha, 200);
        }
      };

      initRecaptcha();
    });
  }, []);

  useEffect(() => {
    if (!initAttemptedRef.current) {
      initAttemptedRef.current = true;
      loadRecaptchaScript();
    }
  }, [loadRecaptchaScript]);

  const executeRecaptcha = useCallback(
    async (action) => {

      // Asegurar que reCAPTCHA esté listo
      if (!window.grecaptcha || !recaptchaReadyRef.current) {
        await loadRecaptchaScript();
      }

      return new Promise((resolve, reject) => {
        if (!window.grecaptcha) {
          console.warn("reCAPTCHA no disponible");
          if (isInIframe()) {
            resolve("iframe-fallback-token");
            return;
          }
          reject(new Error("reCAPTCHA no está cargado"));
          return;
        }

        window.grecaptcha.ready(() => {
          window.grecaptcha
            .execute(RECAPTCHA_SITE_KEY, { action })
            .then((token) => {
              resolve(token);
            })
            .catch((error) => {
              console.error("Error al ejecutar reCAPTCHA:", error);
              if (isInIframe()) {
                console.warn("reCAPTCHA falló en iframe, usando fallback");
                resolve("iframe-fallback-token");
              } else {
                reject(error);
              }
            });
        });
      });
    },
    [loadRecaptchaScript]
  );

  const executeAndValidateRecaptcha = useCallback(
    async (action) => {
      try {
        await loadRecaptchaScript();

        // Ejecutar reCAPTCHA
        const token = await executeRecaptcha(action);

        if (!token || (token.length < 10 && !isInIframe())) {
          throw new Error("Token de reCAPTCHA inválido");
        }

        return { success: true, token };
      } catch (error) {
        console.error("Error en reCAPTCHA:", error);

        if (isInIframe()) {
          console.warn("Permitiendo continuar en iframe sin reCAPTCHA");
          return {
            success: true,
            token: "iframe-error-fallback",
            warning: "reCAPTCHA no disponible en iframe",
          };
        }

        return { success: false, token: null, error: error.message };
      }
    },
    [executeRecaptcha, loadRecaptchaScript]
  );

  const cleanupRecaptcha = useCallback(() => {
    try {
      const recaptchaBadge = document.querySelector(".grecaptcha-badge");
      if (recaptchaBadge) {
        recaptchaBadge.style.display = "none";
      }

      const recaptchaElements = document.querySelectorAll(
        '[style*="recaptcha"]'
      );
      recaptchaElements.forEach((element) => {
        element.style.display = "none";
      });
    } catch (error) {
      console.warn("Error en cleanup de reCAPTCHA:", error);
    }
  }, []);

  return {
    executeRecaptcha,
    executeAndValidateRecaptcha,
    loadRecaptchaScript,
    cleanupRecaptcha,
    isInIframe: isInIframe(),
  };
};
